import { html, css } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { msg } from "@omegagrid/localize";
import { TreeAdapter, TreeEvent, TreeDropdown, TreeSource, SerializedValue, NodeModel } from "@omegagrid/tree";
import { BaseSection } from "./sectionBase";
import { ScenarioDefinition, StatementDefinition, VirtualTreeRow } from "@reporting/rp-types/definition";
import rp from "@reporting/rp-app-host";
import { ContextFilterItem } from "@reporting/rp-types/analytics";

@customElement('rp-fsection-statement')
export class StatementSection extends BaseSection {

	static styles = [...BaseSection.styles, css`
		.row og-tree-dropdown, .row og-dropdown-list {
			flex: 1;
			border: 1px solid var(--og-colortype-accent-border-color);
			background-color: var(--og-accent-color-alpha-30);
			color: var(--og-text-color-3);
		}

		.row:last-child {
			margin-bottom: 0;
		}
	`];

	@query('.dropdown-statement')
	dropdownStatement: TreeDropdown;

	@query('.dropdown-rows')
	dropdownRows: TreeDropdown;

	@query('.dropdown-ab')
	dropdownScenario: TreeDropdown;

	@query('.dropdown-language')
	dropdownLanguage: TreeDropdown;

	@state()
	idStatement: number | null = null;

	@state()
	language: string | null = null;

	@state()
	languages: string[] | null = null;

	private _statements: StatementDefinition[];
	private _statementsRequested = false;
	private _scenarios: ScenarioDefinition[];
	private _rowAdapters = new Map<string, TreeAdapter>();

	async loadLanguages() {
		if (this.languages) return this.languages;
		this.languages = (await rp.ms('definition').get<{lang: string}[]>('languages')).map(l => l.lang);
	}

	getRowAdapter(): TreeAdapter {
		// the rows are translated by the view language, so the cache is keyed by it too
		const key = `${this.idStatement}|${this.language ?? ''}`;
		if (this._rowAdapters.has(key)) return this._rowAdapters.get(key);
		const adapter = new class extends TreeAdapter {
			idStatement: number;
			language: string | null;
			private _rows: TreeSource = null;
			async getTree() : Promise<TreeSource> {
				if (this._rows) return this._rows;
				if (!this.idStatement) return [];
				const rows = await rp.ms('definition').get<VirtualTreeRow[]>(`statements/${this.idStatement}/tree`, {
					...(this.language ? {lang: this.language} : {}),
				});
				this._rows = rows.map(r => ({
					key: r.id_row.toString(),
					value: r.description ?? r.row,
					l: r.level,
				}));
				return this._rows;
			}
			getOptions() {
				return {search: true, selectMode: 'multiple'};
			}
		};
		adapter.idStatement = this.idStatement;
		adapter.language = this.language;
		this._rowAdapters.set(key, adapter);
		return adapter;
	}

	willUpdate() {
		this.idStatement = this.context?.id_statement || null;
		this.language = this.context?.language || null;
		if (!this.languages) this.loadLanguages();
	}

	async updated(props: Map<PropertyKey, unknown>) {
		if (!this._statementsRequested) {
			// the flag is set synchronously so a re-render during the fetch doesn't fire a second request
			this._statementsRequested = true;
			this._statements = await rp.ms('definition').get<StatementDefinition[]>('statements', {
				fields: ['ID_STATEMENT', 'STATEMENT', 'YEARS'],
			});

			this.dropdownStatement.setSourceData(this._statements?.map(s => ({
				k: s.id_statement.toString(),
				v: s.statement,
				props: {
					statement: s,
					minYear: s.years ? Math.min(...s.years) : null,
					maxYear: s.years ? Math.max(...s.years) : null
				}
			})) ?? [], {
				selectMode: 'single',
				checkboxes: false,
				autoClose: true,
				nodeRenderer: (div: HTMLDivElement, node: NodeModel) => {
					const statement = node.properties.statement as StatementDefinition;
					const { minYear, maxYear } = node.properties;
					div.style.display = 'flex';
					div.style.flexDirection = 'row';
					div.innerHTML = `
						<div style="flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${statement.statement}</div>
						${minYear ? 
							`<div style="flex: 0; margin: 0 2px; color: var(--og-accent-color); font-size: 0.8em;">(${minYear}-${maxYear})</div>`
							: ''
						}
					`;
				}
			});

			this.dropdownStatement.value = this.idStatement ? [[this.idStatement.toString(), 0, 1]] : [];
		}

		if (!this._scenarios) {
			this._scenarios = this.module.scenarios;
			this.dropdownScenario.setSourceData(this._scenarios?.map(s => ({
				k: s.a_b,
				v: s.a_b + (s.description ? ` - ${s.description}` : ''),
			})) ?? [], {selectMode: 'multiple'});

			const scenarioFilter = this.getScenarioFilter();
			this.dropdownScenario.value = scenarioFilter?.items as SerializedValue[] || [];
		}

		if (props.has('idStatement') || props.has('language')) {
			this.dropdownRows.adapter = this.getRowAdapter();
			this.dropdownRows.value = this.getRowFilter()?.items as SerializedValue[] || [];
		}

		if (this.dropdownLanguage) {
			this.dropdownLanguage.setSourceData(
				[{k: '', v: `<${msg('default')}>`}, ...this.languages?.map(l => ({k: l, v: l})) ?? []],
				{selectMode: 'single', checkboxes: false, autoClose: true}
			);
			this.dropdownLanguage.value = [[this.context.language || '', 0, 1]];
		}
	}

	getRowFilter() {
		return this.context?.filters?.find(f => f.dim === 'R');
	}

	getScenarioFilter() {
		return this.context?.filters?.find(f => f.dim === 'AB');
	}

	_onStatementChange = (e: TreeEvent) => {
		const value = e.node.key;
		this.idStatement = value ? parseInt(value) : null;
		this.context.filters = this.context.filters?.filter(f => f.dim !== 'R');
		this.updateContext('id_statement', this.idStatement);
	}

	_onScenarioFilterChange = (e: TreeEvent) => {
		let scenarioFilter = this.getScenarioFilter();
		if (!scenarioFilter) {
			scenarioFilter = {dim: 'AB', items: []};
			this.context.filters = [...(this.context.filters || []), scenarioFilter];
		}
		scenarioFilter.items = this.dropdownScenario.selection.serialize() as ContextFilterItem[];
		this.updateContext('filters', this.context.filters);
	}

	_onRowFilterChange = (e: TreeEvent) => {
		let rowFilter = this.getRowFilter();
		if (!rowFilter) {
			rowFilter = {dim: 'R', items: []};
			this.context.filters = [...(this.context.filters || []), rowFilter];
		}
		rowFilter.items = this.dropdownRows.selection.serialize() as ContextFilterItem[];
		this.updateContext('filters', this.context.filters);
	}

	_onLanguageChange = (e: TreeEvent) => {
		this.updateContext('language', e.node?.key);
		// updateContext mutates the context in place - reflect the language into
		// the state directly so the row tree reloads with the new translations
		this.language = this.context?.language || null;
	}

	// .caption="${this.periodDimensionMap.get(filter.dim).description}"
	render = () => html`
		<div class="row">
			<og-tree-dropdown
				.caption="${msg('Statement')}"
				@change="${this._onStatementChange}"
				class="dropdown-statement"
				color="transparent">
			</og-tree-dropdown>
		</div>

		<div class="row">
			<og-tree-dropdown
				.caption="${msg('Scenario')}"
				class="dropdown-ab"
				color="transparent"
				@change="${(e: TreeEvent) => this._onScenarioFilterChange(e)}"
				cross>
			</og-tree-dropdown>
		</div>

		${this.languages?.length ? html`
			<div class="row">
				<og-tree-dropdown
					.caption="${msg('Language')}"
					class="dropdown-language"
					color="transparent"
					.value="${null}"
					@change="${(e: TreeEvent) => this._onLanguageChange(e)}"
					cross>
				</og-tree-dropdown>
			</div>	
		` :  ''}

		<div class="row">
			<og-tree-dropdown
				.caption="${msg('Rows')}"
				class="dropdown-rows"
				color="transparent"
				.adapter="${this.getRowAdapter()}"
				.value="${null}"
				@change="${(e: TreeEvent) => this._onRowFilterChange(e)}"
				cross>
			</og-tree-dropdown>
		</div>
	`;

}
